import { useEffect, useState } from 'react';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import { App as AntApp, Col, DatePicker, Form, Input, Modal, Row, Select } from 'antd';
import dayjs from 'dayjs';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';
import { FormCriarCliente } from '../comercial-pipeline/FormCriarCliente';
import { FormCriarContato } from '../comercial-pipeline/FormCriarContato';
import { SelectComCriar } from '../comercial-pipeline/SelectComCriar';
import { usePerms } from '../../hooks/usePerms';
import { ClienteLead, ContatoLead, Lead, LeadStatus, VendedorLead } from './types';
import {
  CANAIS_LEAD, extrairDadosApi, mensagemErroApi,
  STATUS_LEAD_LABELS,
} from './utils';

export function FormLead({ lead, clientes, onClose, onSalvo }: {
  lead: Lead | null;
  clientes: ClienteLead[];
  onClose: () => void;
  onSalvo: () => void;
}) {
  const { message } = AntApp.useApp();
  const [dados, setDados] = useState({
    cliente_id: lead ? String(lead.cliente_id) : '',
    contato_id: lead?.contato_id ? String(lead.contato_id) : '',
    interesse: lead?.interesse || '',
    status: (lead?.status || 'novo') as LeadStatus,
    responsavel: lead?.responsavel || '',
    vendedor_email: lead?.vendedor_email || '',
    originador_email: lead?.originador_email || '',
    canal_origem: lead?.canal_origem || '',
    proximo_contato: lead?.proximo_contato?.slice(0, 10) || '',
    observacoes: lead?.observacoes || '',
  });
  const [clientesLocais, setClientesLocais] = useState(clientes);
  const [contatos, setContatos] = useState<ContatoLead[]>([]);
  const [vendedores, setVendedores] = useState<VendedorLead[]>([]);
  const [salvando, setSalvando] = useState(false);
  const [criandoCliente, setCriandoCliente] = useState<string | null>(null);
  const [criandoContato, setCriandoContato] = useState<string | null>(null);
  // Empresa nova exige `comercial.empresas.criar` no servidor (05/10/2026).
  // Falha ao carregar a matriz não vira "não pode".
  const { pode, erro: permsErro } = usePerms();
  const podeCriarEmpresa = permsErro || pode('comercial.empresas.criar');

  useEffect(() => {
    (async () => {
      try {
        const session = await fetchAuthSession();
        const email = String((session.tokens?.idToken?.payload as any)?.email || '').toLowerCase();
        const token = session.tokens?.idToken?.toString() || '';
        const resposta = await axios.get(`${API_URL}/admin/usuarios`, { headers: { Authorization: token } });
        const lista = extrairDadosApi(resposta);
        const ativos: VendedorLead[] = (Array.isArray(lista) ? lista : [])
          .filter((usuario: any) => usuario.enabled !== false
            && ((usuario.groups || []).includes('comercial') || (usuario.groups || []).includes('admin')))
          .map((usuario: any) => ({ email: usuario.email, nome: usuario.nome || usuario.email }));
        setVendedores(ativos);
        if (!lead && email) {
          const atual = ativos.find(v => v.email.toLowerCase() === email);
          if (atual) setDados(prev => ({ ...prev, vendedor_email: atual.email, responsavel: atual.nome }));
        }
      } catch { /* O Lead pode ser salvo sem responsável. */ }
    })();
  }, [lead]);

  useEffect(() => {
    if (!dados.cliente_id) { setContatos([]); return; }
    (async () => {
      try {
        const token = await getToken();
        const resposta = await axios.get(
          `${API_URL}/comercial/clientes/${dados.cliente_id}/contatos`,
          { headers: { Authorization: token } },
        );
        setContatos(extrairDadosApi(resposta) || []);
      } catch { setContatos([]); }
    })();
  }, [dados.cliente_id]);

  function trocarEmpresa(id: string) {
    setDados(prev => ({ ...prev, cliente_id: id, contato_id: '' }));
  }

  async function salvar() {
    if (!dados.cliente_id) { message.warning('Escolha a empresa.'); return; }
    if (!dados.interesse.trim()) { message.warning('Informe o que pode virar negócio.'); return; }
    setSalvando(true);
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/clientes`, {
        action: lead ? 'com_leads_atualizar' : 'com_leads_criar',
        id: lead?.id,
        cliente_id: Number(dados.cliente_id),
        contato_id: dados.contato_id ? Number(dados.contato_id) : null,
        interesse: dados.interesse.trim(),
        status: dados.status,
        responsavel: dados.responsavel || null,
        vendedor_email: dados.vendedor_email || null,
        originador_email: dados.originador_email || null,
        canal_origem: dados.canal_origem || null,
        proximo_contato: dados.proximo_contato || null,
        observacoes: dados.observacoes || null,
      }, { headers: { Authorization: token } });
      message.success(lead ? 'Lead atualizado.' : 'Lead criado.');
      onSalvo();
    } catch (erro: any) {
      message.error(mensagemErroApi(erro));
    } finally { setSalvando(false); }
  }

  return (
    <>
      <Modal
        open
        title={lead ? `Editar Lead #${lead.id}` : 'Novo Lead'}
        okText={lead ? 'Salvar' : 'Criar Lead'}
        cancelText="Cancelar"
        confirmLoading={salvando}
        onOk={salvar}
        onCancel={onClose}
        width="94vw"
        style={{ top: 16, maxWidth: 680 }}
        styles={{ body: { maxHeight: 'calc(100vh - 130px)', overflowY: 'auto' } }}
        destroyOnHidden
      >
        <Form layout="vertical" size="small">
          <Form.Item label="Empresa" required>
            <SelectComCriar
              valor={dados.cliente_id}
              opcoes={clientesLocais.map(cliente => ({ id: String(cliente.id), label: cliente.razao_social }))}
              placeholder={podeCriarEmpresa ? 'Buscar empresa ou criar nova...' : 'Buscar empresa...'}
              onSelecionar={trocarEmpresa}
              onCriar={podeCriarEmpresa ? setCriandoCliente : undefined}
            />
          </Form.Item>
          <Form.Item label="Contato principal">
            <SelectComCriar
              valor={dados.contato_id}
              opcoes={contatos.map(contato => ({
                id: String(contato.id),
                label: `${contato.nome}${contato.cargo ? ` — ${contato.cargo}` : ''}`,
              }))}
              placeholder={!dados.cliente_id ? 'Escolha a empresa primeiro' : 'Buscar ou criar contato...'}
              disabled={!dados.cliente_id}
              onSelecionar={id => setDados(prev => ({ ...prev, contato_id: id }))}
              onCriar={setCriandoContato}
              labelCriar="Criar novo contato"
            />
          </Form.Item>
          <Form.Item label="O que pode virar negócio?" required>
            <Input
              autoFocus
              value={dados.interesse}
              onChange={e => setDados(prev => ({ ...prev, interesse: e.target.value }))}
              placeholder="Ex.: Monitoramento hidrológico da barragem"
              maxLength={400}
            />
          </Form.Item>
          {lead && (
            <Form.Item label="Etapa">
              <Select
                value={dados.status}
                onChange={status => setDados(prev => ({ ...prev, status }))}
                options={Object.entries(STATUS_LEAD_LABELS)
                  .filter(([status]) => status !== 'convertido')
                  .map(([value, label]) => ({ value, label }))}
              />
            </Form.Item>
          )}
          <Row gutter={12}>
            <Col xs={24} sm={12}>
              <Form.Item label="Responsável">
                <Select
                  value={dados.vendedor_email || undefined}
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder="Sem responsável"
                  options={vendedores.map(v => ({ value: v.email, label: v.nome }))}
                  onChange={email => {
                    const vendedor = vendedores.find(v => v.email === email);
                    setDados(prev => ({ ...prev, vendedor_email: email || '', responsavel: vendedor?.nome || '' }));
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Próximo contato">
                <DatePicker
                  style={{ width: '100%' }}
                  format="DD/MM/YYYY"
                  value={dados.proximo_contato ? dayjs(dados.proximo_contato) : null}
                  onChange={data => setDados(prev => ({
                    ...prev,
                    proximo_contato: data ? data.format('YYYY-MM-DD') : '',
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col xs={24} sm={12}>
              <Form.Item label="Canal de origem">
                <Select
                  value={dados.canal_origem || undefined}
                  allowClear
                  options={CANAIS_LEAD}
                  onChange={canal => setDados(prev => ({ ...prev, canal_origem: canal || '' }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Quem trouxe o Lead?">
                <Select
                  value={dados.originador_email || undefined}
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder="Opcional"
                  options={vendedores.map(v => ({ value: v.email, label: `${v.nome} (${v.email})` }))}
                  onChange={email => setDados(prev => ({ ...prev, originador_email: email || '' }))}
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="Observações">
            <Input.TextArea
              rows={3}
              value={dados.observacoes}
              onChange={e => setDados(prev => ({ ...prev, observacoes: e.target.value }))}
              placeholder="Contexto, necessidade percebida e próximos passos..."
            />
          </Form.Item>
        </Form>
      </Modal>

      {criandoCliente !== null && (
        <FormCriarCliente
          termo={criandoCliente}
          onClose={() => setCriandoCliente(null)}
          onCriado={novo => {
            setClientesLocais(prev => [...prev, novo]);
            trocarEmpresa(String(novo.id));
            setCriandoCliente(null);
          }}
        />
      )}
      {criandoContato !== null && (
        <FormCriarContato
          termo={criandoContato}
          clienteIdInicial={dados.cliente_id}
          clientes={clientesLocais}
          onClose={() => setCriandoContato(null)}
          onCriado={novo => {
            setContatos(prev => [...prev, novo]);
            setDados(prev => ({ ...prev, contato_id: String(novo.id) }));
            setCriandoContato(null);
          }}
        />
      )}
    </>
  );
}
